import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { Box, Typography } from '@mui/material'
import ModalContainer from '../../ModalContainer'
import { hideModal, showErrorNotification, showSuccesNotification } from '../../../../redux/actions'
import { MUIFormTextInput } from '../../../@Common/Form/TextInput/FormTextInput'
import HelperText from './HelperText'
import AddressForm from '../../../@Common/Form/AddressForm/AddressForm'
import { MUIFormCheckboxInput } from '../../../@Common/Form/CheckboxInput/FormCheckboxInput'
import Label from '../../../@Common/Form/Label/Label'
import ImageUpload from '../../../@Common/Pictures/ImageUpload'
import { getTargetSizeByFileAsync } from '../../../@Common/Pictures/pictureUtils'

export function UpdatePartnersListingModal({
  partnerListing,
  billingRules,
  updatePartnerListing,
  createPicture,
  onUpdated = undefined,
}) {
  const [processing, setProcessing] = useState(false)
  const [showAddressSection, setShowAddressSection] = useState(!!((partnerListing.location?.address !== '' && partnerListing.location !== null)))
  // logoPictureId and logoPictureUrl are hydrated by the backend on the get-listing-by-id path
  const existingLogoId = partnerListing.logoPictureId
  const existingLogoUrl = partnerListing.logoPictureUrl ?? null
  const [logoState, setLogoState] = useState({
    url: existingLogoId ? existingLogoUrl : null,
    id: existingLogoId || 0,
  })
  const [logoError, setLogoError] = useState(null)
  const dispatch = useDispatch()
  const {
    handleSubmit, control, formState: { errors },
  } = useForm({
    defaultValues: {
      name: partnerListing.name,
      website: partnerListing.website,
      commissionRate: billingRules.commissionRate,
      address: partnerListing.location?.address || '',
      city: partnerListing.location?.city || '',
      state: partnerListing.location?.state || '',
      zipCode: partnerListing.location?.zip || '',
      locationName: partnerListing.location?.locationName || '',
      hasAddress: showAddressSection,
    },
  })

  const onSubmit = async (values) => {
    setProcessing(true)
    try {
      // start from the current logo selection so a delete (id=0) actually clears it
      let logoId = logoState.id
      if (logoState.url && typeof logoState.url !== 'string') {
        const pictureFormData = new window.FormData()
        const targetSize = await getTargetSizeByFileAsync(logoState.url)
        pictureFormData.append('file', logoState.url)
        pictureFormData.append('seoFilename', 'listing-header-logo')
        pictureFormData.append('targetSize', targetSize)
        const picture = await createPicture(pictureFormData)
        logoId = picture.data.id
      }

      const pl = {
        id: partnerListing.id,
        name: values.name,
        description: partnerListing.description,
        website: values.website,
        status: partnerListing.status,
        logoId,
        eligibleForGXMarketplace: partnerListing.eligibleForGXMarketplace,
        billingRules: {
          commissionRate: values.commissionRate,
          flatRate: null,
        },
        listingLocation: {
          address: showAddressSection ? values.address : null,
          city: showAddressSection ? values.city : null,
          state: showAddressSection ? values.state : null,
          zip: showAddressSection ? values.zipCode : null,
          locationName: showAddressSection ? values.locationName : null,
        },
      }

      await updatePartnerListing(pl).unwrap()
      setProcessing(false)
      onUpdated?.()
      dispatch(showSuccesNotification('Partner listing updated successfully.'))
      dispatch(hideModal())
    } catch (err) {
      setProcessing(false)
      console.log('Form error', err)
      dispatch(showErrorNotification('Something went wrong. Please try that again.'))
      dispatch(hideModal())
    }
  }

  return (
    <ModalContainer
      title="Edit Listing"
      modal
      maxWidth="sm"
      fullWidth="true"
      processing={processing}
      actions={{
        primaryAction: { label: 'Save', onClick: handleSubmit(onSubmit) },
        secondaryAction: { label: 'Cancel', onClick: () => dispatch(hideModal()) },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box mb="16px">
          <MUIFormTextInput
            name="name"
            label="Listing Name"
            autoFocus
            control={control}
            shrinkLabel
            rules={{
              required: {
                value: true,
                message: 'Listing name is required',
              },
            }}
          />
          <HelperText>
            Typically this is the name of your organization.
            Keep in mind, the listing name will be visible to your customers.
            You can always change this later.
          </HelperText>
        </Box>
        <MUIFormTextInput
          name="website"
          label="Website URL"
          control={control}
          rules={{
            required: {
              value: true,
              message: 'Website URL is required',
            },
            pattern: {
              value: /^https?:\/\//,
              message: 'Website URL must start with http:// or https://',
            },
          }}
        />
        <HelperText>
          Please enter the URL of the website where you intend to deploy GovX verification.
        </HelperText>
        <Box mt="22px">
          <Typography sx={{ margin: '0px', fontWeight: 'bold', mb: '.5rem' }}>
            Logo
          </Typography>
          <Box display="flex" sx={{ gap: '24px', alignItems: 'flex-start' }}>
            <Box
              component="ul"
              sx={{
                paddingLeft: '1.25rem',
                margin: 0,
                color: '#4f4f4f',
                fontSize: '0.875rem',
              }}
            >
              <li>Supports JPG, JPEG, PNG</li>
              <li>Max file size 2MB</li>
            </Box>
            <ImageUpload
              variant="logo"
              imageUrl={logoState.url}
              imageId={logoState.id}
              previewId="listingLogoImagePreview"
              maxSizeMB={2}
              error={logoError}
              validate={setLogoError}
              onEdit={(file) => setLogoState({ url: file, id: 0 })}
              onDelete={() => { setLogoState({ url: null, id: 0 }); setLogoError(null) }}
            />
          </Box>
        </Box>
        <Box mt="16px">
          <MUIFormCheckboxInput
            onClick={() => setShowAddressSection(!showAddressSection)}
            key="hasAddress"
            name="hasAddress"
            label="Listing has a physical location (i.e. venue or store)"
            control={control}
            gxid="hasAddress"
          />
        </Box>
        {
          showAddressSection
          && (
            <Box mt="5px">
              <AddressForm control={control} errors={errors} isRequired={showAddressSection} />
            </Box>
          )
        }

        <Box margin={{ marginTop: showAddressSection ? '30px' : '16px' }}>
          <Box display="flex" sx={{ justifyContent: 'space-between' }}>
            <Box style={{ fontSize: '.875rem' }}>
              <Typography style={{ margin: '0px' }}>
                <Label>Billing Rules</Label>
              </Typography>
              <Box>
                Defines the commission rate on a sale
              </Box>
            </Box>
            <Box display="flex" sx={{ alignItems: 'center', width: '50%' }}>
              <Box width="45%">
                <MUIFormTextInput
                  control={control}
                  type="number"
                  name="commissionRate"
                  label="Commission"
                  rules={{
                    required: {
                      value: true,
                      message: 'Commission rate is required',
                    },
                    min: 0,
                    max: 100,
                  }}
                  suffix="%"
                  shrinkLabel="true"
                />
              </Box>
            </Box>
          </Box>
          {/* <Box style={{ paddingLeft: '50%' }}>
                        {formInfo.billingFlatRate && formInfo.billingFlatRate.touched &&
            formErrors.billingFlatRate &&
            <FormHelperText error={true}>Billing Flate Rate is Required
                          </FormHelperText>}
                    </Box> */}
        </Box>
      </form>
    </ModalContainer>
  )
}

UpdatePartnersListingModal.propTypes = {
  partnerListing: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    website: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    logoPictureId: PropTypes.number,
    logoPictureUrl: PropTypes.string,
    commissionRate: PropTypes.number.isRequired,
    eligibleForGXMarketplace: PropTypes.bool,
    billingRules: PropTypes.shape({
      commissionRate: PropTypes.number.isRequired,
      flatRate: PropTypes.number,
    }).isRequired,
    location: PropTypes.shape({
      address: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
      locationName: PropTypes.string,
    }),
  }).isRequired,
  billingRules: PropTypes.shape({
    commissionRate: PropTypes.number.isRequired,
    flatRate: PropTypes.number,
  }).isRequired,
  updatePartnerListing: PropTypes.func.isRequired,
  createPicture: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
}

export default UpdatePartnersListingModal
